import { FxToggles } from "@/components/casino/FxToggles";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { buildConnectUrl, connectToDeriv, AFFILIATE_URL } from "@/lib/api";
import { Sparkles, LogOut, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";

const links = [
  { to: "/", label: "Home" },
  { to: "/dashboard", label: "Lobby" },
  { to: "/arcade", label: "Arcade" },
  { to: "/charts", label: "Charts" },
  { to: "/history", label: "History" },
];

export default function SiteNav() {
  const loc = useLocation();
  const nav = useNavigate();
  const [session, setSession] = useState({ id: "", loginid: "", type: "" });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const read = () => setSession({
      id: localStorage.getItem("naevo_session") || "",
      loginid: localStorage.getItem("naevo_loginid") || "",
      type: localStorage.getItem("naevo_account_type") || "",
    });
    read();
    window.addEventListener("storage", read);
    window.addEventListener("naevo-session-change", read);
    return () => {
      window.removeEventListener("storage", read);
      window.removeEventListener("naevo-session-change", read);
    };
  }, [loc.pathname]);

  const isConnected = Boolean(session.id && session.loginid);
  const isUnverified = session.loginid === "UNKNOWN";
  const isDemo = session.type === "demo";

  function disconnect() {
    ["naevo_session","naevo_loginid","naevo_currency","naevo_token","naevo_account_type","naevo_account_list","naevo_accounts","naevo_active_account","naevo_balance"].forEach((k) => localStorage.removeItem(k));
    window.dispatchEvent(new Event("naevo-session-change"));
    nav("/");
  }

  return (
    <header className="sticky top-0 z-40 glass border-b border-gold/20">
      <div className="mx-auto max-w-7xl flex items-center justify-between gap-2 px-4 sm:px-6 py-4">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-logo-link">
          <div className="h-9 w-9 rounded-full btn-gold flex items-center justify-center">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="leading-tight">
            <div className="font-casino text-base sm:text-lg text-gold-grad whitespace-nowrap">Naevo <span>Casino</span></div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">Powered by Deriv</div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`text-sm transition-colors ${loc.pathname === l.to ? "text-magenta" : "text-white/70 hover:text-white"}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>
        {/* Mobile hamburger — opens the same links in a slide-down sheet */}
        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="md:hidden h-9 w-9 rounded-full flex items-center justify-center border border-white/10 bg-white/5"
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>
        <div className="flex items-center gap-2">
          <div className="hidden sm:block"><FxToggles /></div>
          {!isConnected ? (
            <>
              <a
                href={AFFILIATE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-signup-cta"
                className="hidden sm:inline-block btn-ghost rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap"
              >
                Sign up
              </a>
              <a
                href={buildConnectUrl()}
                onClick={(e) => { e.preventDefault(); connectToDeriv(); }}
                data-testid="nav-connect-cta"
                className="btn-magenta rounded-full px-5 py-2 text-sm font-medium whitespace-nowrap"
              >
                Connect
              </a>
            </>
          ) : (
            <>
              <Link
                to="/dashboard"
                data-testid="nav-account-badge"
                className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 pl-3 pr-4 py-1.5 hover:bg-white/10 transition"
              >
                <span className={`h-2 w-2 rounded-full ${isUnverified ? "bg-white/40" : isDemo ? "bg-yellow-400" : "bg-emerald-400"} animate-pulse`} />
                <span className="text-xs text-white/50 uppercase tracking-wider hidden sm:inline">
                  {isUnverified ? "Refreshing" : isDemo ? "Demo" : "Real"}
                </span>
                <span className="text-sm font-medium text-white">
                  {isUnverified ? "…" : session.loginid}
                </span>
              </Link>
              <button
                onClick={disconnect}
                data-testid="nav-disconnect"
                className="btn-ghost rounded-full p-2.5"
                aria-label="Disconnect"
                title="Disconnect"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>
      {/* Mobile links panel */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/10 bg-[#0C0714]" data-testid="nav-mobile-panel">
          <div className="mx-auto max-w-7xl px-6 py-3 flex flex-col gap-1">
            <div className="flex items-center justify-between py-2 border-b border-white/5 mb-1">
              <span className="text-xs uppercase tracking-[0.25em] text-gold/70">Sound & vibration</span>
              <FxToggles />
            </div>
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                className={`py-2 text-sm ${loc.pathname === l.to ? "text-magenta" : "text-white/80"}`}
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
